import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type ECommerceProduct } from '@hooks-api/types/dataModel/products'
import { Box } from '@pets-ui/Box/Box'
import { ButtonBase } from '@pets-ui/ButtonBase/ButtonBase'
import { Collapse } from '@pets-ui/Collapse/Collapse'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowDownLarge, ArrowUpLarge } from '@pets-ui/icons'
import { Image } from '@pets-ui/Image/Image'
import { PlaceholderNoImage } from '@pets-ui/Placeholder/PlaceholderNoImage'
import { Skeleton } from '@pets-ui/Skeleton/Skeleton'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { sumOfArray } from '@utils/sumOfArray'
import { useMemo, useState } from 'react'
import { CouponProductSelectorItemVariationListProducts } from './CouponProductSelectorItemVariationListProducts'
import { useContextCouponProductSelector } from './CouponProductSelectorProvider'

export interface CouponProductSelectorItemVariationListProps {
  name: string
  thumbnail: string
  products: ECommerceProduct[]
  onToggleCouponProduct?: (isOpen: boolean) => void
}

export const CouponProductSelectorItemVariationList = (props: CouponProductSelectorItemVariationListProps) => {
  const { name, thumbnail, products, onToggleCouponProduct } = props
  const { cartItems } = useContextCouponProductSelector()
  const [isOpen, setIsOpen] = useState(false)
  const isAllOutOfStock = useMemo(() => products.every(product => Number(product.stock ?? 0) <= 0), [products])

  const productsMap = useMemo(() =>
    products.reduce<Record<string, ECommerceProduct>>((acc, product) => {
      if (product.product_id) {
        acc[product.product_id] = product
      }
      return acc
    }, {}), [products])

  const totalQuantity = useMemo(() => sumOfArray(cartItems, (acc, { cartItemId, quantity = 0 }) =>
    productsMap[cartItemId] ? acc + quantity : acc),
  [cartItems, productsMap])

  const handleCollapseClick = useCallbackEvent(() => {
    setIsOpen(!isOpen)
    onToggleCouponProduct?.(!isOpen)
  })

  return (
    <VStack>
      <ButtonBase disableRipple onClick={handleCollapseClick}>
        <HStack gap={2} alignItems="center" width="100%" p={2} flexWrap="nowrap">
          <Image
            src={thumbnail}
            width={72}
            height={72}
            loadingFallback={<Skeleton width={72} height={72} />}
            fallback={<PlaceholderNoImage width={72} height={72} />}
            alt={name}
          />
          <VStack gap={1}>
            <Text variant="subtitle">{name}</Text>
            {isAllOutOfStock
              ? <Text variant="body" color={colors.red600}>已售完</Text>
              : totalQuantity > 0 && (
                <Text variant="body" color={colors.green400}>
                  已選擇：
                  {totalQuantity}
                </Text>
              )}
          </VStack>
          <Box flexGrow={1} />
          {isOpen
            ? <ArrowUpLarge fontSize={16} color={colors.grey600} />
            : <ArrowDownLarge fontSize={16} color={colors.grey600} />}
        </HStack>
      </ButtonBase>
      <Collapse in={isOpen}>
        <CouponProductSelectorItemVariationListProducts products={products} />
      </Collapse>
    </VStack>
  )
}
